import React from 'react';
import { Card, DisplayText, List, Button } from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { PlatformType, EnrollmentType } from '../types';
import { FormattedMessage } from 'react-intl';
import { Loader } from '../../../index';

interface QRCodeDisplayProps {
  qrConfigData: string | null;
  qrLoading: boolean;
  generateQRLoading: boolean;
  showQRCard: boolean;
  activeView: PlatformType;
  enrollmentType: EnrollmentType[];
  selectedDeviceType: string;
  enrollmentUrl?: string;
  validationCode?: string;
  children?: React.ReactNode; // For email sender component
}

const QRCodeDisplay: React.FC<QRCodeDisplayProps> = ({
  qrConfigData,
  qrLoading,
  generateQRLoading,
  showQRCard,
  activeView,
  enrollmentType,
  selectedDeviceType,
  enrollmentUrl,
  children,
  validationCode
}) => {
  const handleDownloadMobileConfig = () => {
    if (enrollmentUrl) {
      // Open the enrollment URL in a new tab to download the .mobileconfig file
      window.open(enrollmentUrl, '_blank');
    }
  };

  const renderInstructions = () => {
    const currentEnrollmentType = enrollmentType[0]; // Get the first enrollment type

    if (activeView === 'IOS') {
      if (selectedDeviceType === 'Mac') {
        return (
          <>
            <List.Item>
              <FormattedMessage id="mdm.mac.instruction1" />
            </List.Item>
            <List.Item>
              <FormattedMessage id="mdm.mac.instruction2" />
            </List.Item>
          </>
        );
      }
      return (
        <>
          <List.Item>
            <FormattedMessage id="mdm.ios.instruction1" />
          </List.Item>
          <List.Item>
            <FormattedMessage id="mdm.ios.instruction2" />
          </List.Item>
        </>
      );
    }

    // Android instructions based on enrollment type
    if (activeView === 'ANDROID') {
      switch (currentEnrollmentType) {
        case 'fully-managed':
          return (
            <>
              <List.Item>
                <FormattedMessage id="mdm.android.fullyManaged.instruction1" />
              </List.Item>
              <List.Item>
                <FormattedMessage id="mdm.android.fullyManaged.instruction2" />
              </List.Item>
            </>
          );

        case 'BYOD':
          return (
            <>
              <List.Item>
                <FormattedMessage id="mdm.android.byod.instruction1" />
              </List.Item>
              <List.Item>
                <FormattedMessage id="mdm.android.byod.instruction2" />
              </List.Item>
              <List.Item>
                <FormattedMessage id="mdm.android.byod.instruction3" />
              </List.Item>
              <List.Item>
                <FormattedMessage id="mdm.android.byod.instruction4" />
              </List.Item>
              <List.Item>
                <FormattedMessage id="mdm.android.byod.instruction5" />{' '}
                <DisplayText weight="bold" size="large">
                  {validationCode}
                </DisplayText>
              </List.Item>
            </>
          );

        default:
          // Default to fully-managed instructions
          return (
            <>
              <List.Item>
                <FormattedMessage id="mdm.android.fullyManaged.instruction1" />
              </List.Item>
              <List.Item>
                <FormattedMessage id="mdm.android.fullyManaged.instruction2" />
              </List.Item>
            </>
          );
      }
    }

    // Fallback for any other case
    return (
      <>
        <List.Item>
          <FormattedMessage id="mdm.android.default.instruction" />
        </List.Item>
      </>
    );
  };

  // Initial state - Show SVG icon when no QR data
  if (!generateQRLoading && !showQRCard) {
    const iconName = selectedDeviceType === 'Mac' ? 'MacProfile' : 'QRCode';
    return (
      <Card noShadow border="secondary">
        <div className="p2030 flex actr g20">
          <div className="w170p h170 rounded-2xl flex actr jctr border-gray">
            <SVGIcon name={iconName} size="xl" />
          </div>
          <DisplayText weight="Regular" size="large">
            <FormattedMessage
              id={
                selectedDeviceType === 'Mac'
                  ? 'mdm.enrollment.profile.instruction'
                  : 'mdm.enrollment.qr.instruction'
              }
            />
          </DisplayText>
        </div>
      </Card>
    );
  }

  // Loading state - Show loader with same dimensions
  if (generateQRLoading) {
    return (
      <Card noShadow border="secondary">
        <div className="p2030 flex actr jctr">
          <div className="w170p h170 flex actr jctr">
            <Loader />
          </div>
        </div>
      </Card>
    );
  }

  // Loaded state - Show QR code with instructions
  return (
    <Card noShadow border="secondary">
      <div className="p20 flex space-between g10">
        <div className="flex g10">
          <div>
            <div className="bg-white rounded w170p h170 flex actr jctr">
              {!qrLoading &&
                qrConfigData &&
                (selectedDeviceType === 'Mac' ? (
                  <div className="w170p h170 flex actr jctr border-gray rounded-2xl">
                    <SVGIcon name="Profile" />
                  </div>
                ) : (
                  <img
                    src={`data:image/png;base64,${qrConfigData}`}
                    alt="QR Code"
                    className="rounded-lg shadow-md w170p h170"
                  />
                ))}
            </div>
          </div>
          <div className="mt10">
            <DisplayText weight="Regular" size="large">
              <FormattedMessage id="mdm.installer.instructionToUse" />
            </DisplayText>
            <div className="maxW400 mt10">
              <List type="number">{renderInstructions()}</List>
            </div>
          </div>
        </div>
        <div className="flex mt20">
          <div className="min-width-250">
            {activeView === 'IOS' && enrollmentUrl && (
              <Button
                icon={<SVGIcon name="Download" />}
                className="mb10 w100"
                onClick={handleDownloadMobileConfig}
                disabled={!enrollmentUrl}
              >
                <FormattedMessage
                  id={
                    selectedDeviceType === 'Mac'
                      ? 'mdm.installer.profileFileDownload'
                      : 'mdm.installer.downloadMobileConfig'
                  }
                />
              </Button>
            )}
          </div>
          {children}
        </div>
      </div>
    </Card>
  );
};

export default QRCodeDisplay;
